import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import { ORG_BASE } from '@/pages/services/organizations/organizations-service';
import { ActionBar, Breadcrumbs, BreadcrumbItem, Container, PageHeader, Section, SettingRow } from '@vnetwork/ui/layout';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
  Input,
  Textarea,
  Switch,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import {
  useTeam,
  useTeamMembers,
  useUpdateTeam,
  useDeleteTeam,
} from '@/api/teams';
import { TeamMembersTab } from './TeamMembersTab';
import { TeamPermissionsTab } from './TeamPermissionsTab';
import { formatDateTime } from './shared';

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
      <dt style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{label}</dt>
      <dd
        style={{
          margin: 0,
          fontSize: 'var(--vn-font-size-md)',
          fontFamily: mono ? 'var(--vn-font-mono)' : undefined,
        }}
      >
        {value || '—'}
      </dd>
    </div>
  );
}

export function TeamDetail() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { show } = useToast();
  const { user } = useAuth();

  const { data: team, isLoading } = useTeam(teamId);
  const { data: members = [] } = useTeamMembers(teamId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Sync the edit form whenever the team loads/changes.
  useEffect(() => {
    if (team) {
      setName(team.team_name);
      setDescription(team.team_description ?? '');
      setActive(team.active);
    }
  }, [team]);

  const isOwner = !!team && !!user?.email && team.owner === user.email;
  const dirty =
    !!team &&
    (name.trim() !== team.team_name ||
      description !== (team.team_description ?? '') ||
      active !== team.active);

  const update = useUpdateTeam(teamId ?? '', {
    onSuccess: () => show({ tone: 'success', title: 'Team updated' }),
    onError: () => show({ tone: 'danger', title: 'Could not update team' }),
  });
  const remove = useDeleteTeam({
    onSuccess: () => {
      show({ tone: 'success', title: 'Team deleted' });
      navigate(`${ORG_BASE}/teams`);
    },
    onError: () => show({ tone: 'danger', title: 'Could not delete team' }),
  });

  if (isLoading) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 36, width: 240, marginBottom: 'var(--vn-space-6)' }} />
        <Skeleton style={{ height: 220 }} />
      </Container>
    );
  }

  if (!team) {
    return (
      <Container size="xl">
        <EmptyState
          icon={<Users size={28} />}
          title="Team not found"
          description="It may have been deleted, or you don't have access to it."
          actions={
            <Button
              variant="secondary"
              size="sm"
              startIcon={<ArrowLeft size={15} />}
              onClick={() => navigate(`${ORG_BASE}/teams`)}
            >
              Back to teams
            </Button>
          }
        />
      </Container>
    );
  }

  function handleSave() {
    if (!name.trim()) return;
    update.mutate({
      team_name: name.trim(),
      team_description: description.trim(),
      active,
    });
  }

  // Pending invitees haven't accepted yet — count them apart from real members so
  // the headcount isn't inflated by outstanding invitations.
  const activeCount = members.filter((m) => !m.pending).length;
  const pendingCount = members.length - activeCount;
  const memberCount = members.length;
  const canDelete = isOwner && memberCount === 0;

  return (
    <Container size="xl">
      <PageHeader
        icon={<Users size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild>
              <a href={`${ORG_BASE}/teams`} onClick={(e) => { e.preventDefault(); navigate(`${ORG_BASE}/teams`); }}>Teams</a>
            </BreadcrumbItem>
            <BreadcrumbItem current>{team.team_name}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={team.team_name}
        actions={
          <StatusPill tone={team.active ? 'success' : 'muted'}>
            {team.active ? 'Active' : 'Inactive'}
          </StatusPill>
        }
      />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
        </TabsList>

        {/* ---------------- Overview ---------------- */}
        <TabsContent value="overview">
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Section
              title="Basic information"
              description={
                isOwner
                  ? 'Edit your team name, description, and status.'
                  : 'Team name and description.'
              }
              style={{ marginBottom: 0 }}
            >
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <SettingRow
                title="Name & description"
                description="The display name and an optional note about what this team does."
              >
                <div
                  style={{
                    display: 'grid',
                    gap: 'var(--vn-space-4)',
                    maxWidth: 480,
                    marginTop: 'var(--vn-space-3)',
                  }}
                >
                  <Field label="Team name">
                    {(p) => (
                      <Input
                        {...p}
                        value={name}
                        disabled={!isOwner}
                        onChange={(e) => setName(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Description">
                    {(p) => (
                      <Textarea
                        {...p}
                        rows={3}
                        value={description}
                        disabled={!isOwner}
                        placeholder={isOwner ? 'What does this team do?' : '—'}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              </SettingRow>
              {isOwner && (
                <SettingRow
                  title="Active"
                  description="Inactive teams can't be used to grant access."
                  control={<Switch checked={active} onCheckedChange={setActive} />}
                />
              )}
              </div>
              {isOwner && (
                <ActionBar
                  sticky={false}
                  start={
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!dirty || update.isPending}
                      onClick={() => {
                        setName(team.team_name);
                        setDescription(team.team_description ?? '');
                        setActive(team.active);
                      }}
                    >
                      Reset
                    </Button>
                  }
                  end={
                    <Button size="sm" disabled={!dirty || !name.trim() || update.isPending} onClick={handleSave}>
                      {update.isPending ? 'Saving…' : 'Save changes'}
                    </Button>
                  }
                />
              )}
            </Section>

            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardBody>
                <dl
                  style={{
                    display: 'grid',
                    gap: 'var(--vn-space-4)',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    margin: 0,
                  }}
                >
                  <DetailRow label="Team ID" value={team.id} mono />
                  <DetailRow label="Slug" value={team.team_slug} mono />
                  <DetailRow label="Type" value={team.team_type} />
                  <DetailRow label="Owner" value={team.owner} />
                  <DetailRow label="Organization" value={team.organization} mono />
                  <DetailRow
                    label="Members"
                    value={
                      pendingCount > 0
                        ? `${activeCount} active · ${pendingCount} pending`
                        : String(activeCount)
                    }
                  />
                  <DetailRow label="Created" value={formatDateTime(team.created_at)} />
                  <DetailRow label="Last updated" value={formatDateTime(team.updated_at)} />
                </dl>
              </CardBody>
            </Card>

            {isOwner && (
              <Section
                title="Danger zone"
                description="Destructive actions for this team."
                style={{ marginBottom: 0 }}
              >
                <SettingRow
                  title="Delete this team"
                  description={
                    canDelete
                      ? 'Permanently delete this team. This action cannot be undone.'
                      : `Remove all ${memberCount} member${memberCount === 1 ? '' : 's'} before deleting this team.`
                  }
                  control={
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={!canDelete}
                      onClick={() => setConfirmDelete(true)}
                    >
                      Delete team
                    </Button>
                  }
                />
              </Section>
            )}
          </div>
        </TabsContent>

        {/* ---------------- Members ---------------- */}
        <TabsContent value="members">
          <TeamMembersTab teamId={team.id} isOwner={isOwner} />
        </TabsContent>

        {/* ---------------- Permissions ---------------- */}
        <TabsContent value="permissions">
          <TeamPermissionsTab
            teamId={team.id}
            isOwner={isOwner}
            organization={team.organization}
          />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete team?"
        description={`This permanently deletes “${team.team_name}”. This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        requireText={team.team_name}
        loading={remove.isPending}
        onConfirm={() => remove.mutate(team.id)}
      />
    </Container>
  );
}
